/**
 * CrossingsPanel - Panel des utilisateurs croisés
 * Affiche la liste des personnes croisées avec localisation GPS
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { api } from '../../../services/api';
import { toast } from 'sonner';
import { CrossingsMap } from './CrossingsMap';

export function CrossingsPanel({ isDarkMode, customerId }) {
  const [crossings, setCrossings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedCrossing, setSelectedCrossing] = useState(null);
  
  useEffect(() => {
    loadCrossings();
    checkLocationStatus();
  }, [customerId]);
  
  const checkLocationStatus = async () => {
    try {
      const profile = await api.getHeartbeatsProfile(customerId);
      setLocationEnabled(profile?.locationEnabled || false);
      if (profile?.latitude && profile?.longitude) {
        setUserLocation({ lat: profile.latitude, lng: profile.longitude });
      }
    } catch (error) {
      console.error('Erreur vérification localisation:', error);
    }
  };
  
  const loadCrossings = async () => {
    try {
      const res = await api.getCrossings(customerId, 7);
      setCrossings(res.crossings || []);
    } catch (error) {
      console.error('Erreur chargement croisements:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const enableLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Géolocalisation non supportée');
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await api.updateHeartbeatsProfile(customerId, { locationEnabled: true });
          await api.updateLocation(customerId, position.coords.latitude, position.coords.longitude);
          setLocationEnabled(true);
          setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
          toast.success('📍 Localisation activée !');
        } catch (error) {
          toast.error('Erreur activation');
        }
      },
      (error) => {
        toast.error('Permission refusée');
      }
    );
  };
  
  const handleWave = async (crossingId, displayName) => {
    try {
      await api.waveFromCrossing(crossingId, customerId);
      toast.success(`👋 Coucou envoyé à ${displayName} !`);
      loadCrossings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  const demoLocations = crossings.length > 0 ? crossings : [
    { id: 'demo1', displayName: 'Marie', avatar: '👩', count: 5, lat: 45.899, lng: 6.129, locationName: 'La Petite Épicerie' },
    { id: 'demo2', displayName: 'Thomas', avatar: '👨', count: 3, lat: 45.898, lng: 6.127, locationName: 'Place du marché' },
    { id: 'demo3', displayName: 'Julie', avatar: '👧', count: 2, lat: 45.900, lng: 6.130, locationName: 'Parc du centre' },
  ];
  
  const defaultCenter = userLocation || { lat: 45.899, lng: 6.129 };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          🗺️ Vous vous êtes croisés
        </h3>
        <div className="flex gap-2">
          {locationEnabled && showMap && (
            <button
              onClick={() => setShowHeatmap(!showHeatmap)}
              className={`text-sm px-3 py-1 rounded-full transition-all ${
                showHeatmap 
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' 
                  : isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-700'
              }`}
              title="Afficher/masquer la Heat Map"
            >
              🔥 Heat
            </button>
          )}
          {locationEnabled && (
            <button
              onClick={() => setShowMap(!showMap)}
              className={`text-sm px-3 py-1 rounded-full transition-all ${
                showMap 
                  ? 'bg-pink-500 text-white' 
                  : isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              {showMap ? '📋 Liste' : '🗺️ Carte'}
            </button>
          )}
          {!locationEnabled && (
            <button
              onClick={enableLocation}
              className="text-sm px-3 py-1 rounded-full bg-blue-500 text-white"
            >
              📍 Activer GPS
            </button>
          )}
        </div>
      </div>
      
      {/* CARTE INTERACTIVE */}
      {showMap && locationEnabled && (
        <div className="rounded-xl overflow-hidden shadow-lg" style={{ height: '300px' }}>
          <CrossingsMap 
            center={defaultCenter}
            crossings={demoLocations}
            userLocation={userLocation}
            isDarkMode={isDarkMode}
            onSelectCrossing={setSelectedCrossing}
            showHeatmap={showHeatmap}
          />
        </div>
      )}
      
      {/* Popup du croisement sélectionné */}
      {selectedCrossing && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-xl ${isDarkMode ? 'bg-gradient-to-r from-pink-900/50 to-orange-900/50 border border-pink-500/30' : 'bg-gradient-to-r from-pink-50 to-orange-50 border border-pink-200'}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-orange-400 flex items-center justify-center text-xl">
                {selectedCrossing.avatar}
              </div>
              <div>
                <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {selectedCrossing.displayName}
                </div>
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  📍 {selectedCrossing.locationName} • {selectedCrossing.count}x
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleWave(selectedCrossing.id, selectedCrossing.displayName)}
                className="px-3 py-1.5 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 text-white text-sm font-medium"
              >
                👋 Coucou
              </motion.button>
              <button
                onClick={() => setSelectedCrossing(null)}
                className={`px-2 py-1.5 rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}
              >
                ✕
              </button>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* LISTE DES CROISEMENTS */}
      {!showMap && (
        <>
          {crossings.length === 0 ? (
            <div className={`text-center py-8 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <MapPin className="w-16 h-16 mx-auto mb-3 opacity-30" />
              <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Pas encore de croisements
              </p>
              <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Active ta localisation pour découvrir<br />les gens que tu croises au quotidien !
              </p>
              {locationEnabled && (
                <p className={`text-xs mt-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                  ✅ GPS activé - On te notifie dès qu'on détecte un croisement !
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {crossings.map((crossing) => (
                <motion.div
                  key={crossing.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}
                >
                  <div className="flex items-center gap-3">
                    {crossing.mainPhoto ? (
                      <img
                        src={crossing.mainPhoto}
                        alt={crossing.displayName}
                        className="w-14 h-14 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-400 to-orange-400 flex items-center justify-center text-2xl">
                        {crossing.avatar}
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {crossing.displayName}
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        🔄 Croisé(e) {crossing.count}x cette semaine
                      </div>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        📍 {crossing.locationName || 'Dans le quartier'}
                      </div>
                    </div>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleWave(crossing.id, crossing.displayName)}
                      className="px-4 py-2 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 text-white text-sm font-medium"
                    >
                      👋 Coucou
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}
      
      {/* Stats rapides */}
      {locationEnabled && crossings.length > 0 && (
        <div className={`grid grid-cols-3 gap-3 p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-pink-50 to-orange-50'}`}>
          <div className="text-center">
            <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {crossings.length}
            </div>
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Personnes croisées
            </div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {crossings.reduce((sum, c) => sum + (c.count || 0), 0)}
            </div>
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Croisements total
            </div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              📍
            </div>
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Lieux fréquentés
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CrossingsPanel;
